import { create } from 'zustand';

import type { UserPreferencesRepository } from '@/adapters/database/types';
import type { Logger } from '@/adapters/logger/types';
import type {
  PushNotificationAdapter,
  PushNotificationPermissionStatus,
} from '@/adapters/pushNotification/types';

export interface NotificationPromptAdapters {
  pushNotification: PushNotificationAdapter;
  userPreferences: UserPreferencesRepository;
  logger: Logger;
}

export interface NotificationPromptState {
  isVisible: boolean;
  permissionStatus: PushNotificationPermissionStatus | null;
}

export interface NotificationPromptActions {
  evaluate: (userId: string) => Promise<void>;
  skip: (userId: string) => Promise<void>;
  dismiss: () => void;
}

const getPromptSkippedStorageKey = (userId: string) => `notifications.promptSkipped:${userId}`;

const createNotificationPromptStore = (adapters: NotificationPromptAdapters) =>
  create<NotificationPromptState & NotificationPromptActions>((set) => ({
    isVisible: false,
    permissionStatus: null,

    evaluate: async (userId: string) => {
      try {
        const status = await adapters.pushNotification.getPermissionStatus();

        if (status === 'granted') {
          set({ isVisible: false, permissionStatus: status });
          return;
        }

        const key = getPromptSkippedStorageKey(userId);
        const skippedValue = await adapters.userPreferences.getValue(userId, key);

        set({ isVisible: skippedValue !== 'true', permissionStatus: status });
      } catch (error) {
        adapters.logger.captureException(error);
        set({ isVisible: false });
      }
    },

    skip: async (userId: string) => {
      set({ isVisible: false });

      try {
        const key = getPromptSkippedStorageKey(userId);
        await adapters.userPreferences.setValue(userId, key, 'true');
      } catch (error) {
        adapters.logger.captureException(error);
      }
    },

    dismiss: () => set({ isVisible: false }),
  }));

export { createNotificationPromptStore };
